import { useState } from 'react';
import { useParams } from 'react-router';

import { Error, WarningAmber } from '@mui/icons-material';
import DeleteOutlined from '@mui/icons-material/DeleteOutline';
import {
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarDensitySelector,
  type GridRowSelectionModel
} from '@mui/x-data-grid-pro';

import { useQueryClient } from '@tanstack/react-query';

import Button from '@/components/button';
import Dialog from '@/components/dialog';
import {
  useDeleteEvaluationPosts,
  useExportEvaluationCSV,
  useFetchEvaluation,
  useGetEvaluationReport
} from '@/hooks/queries/evaluations';
import { useUrlParams } from '@/hooks/urlParams';
import useAlert from '@/hooks/useAlert';
import { useRenderPDF } from '@/hooks/useRenderPDF';
import { Evaluation } from '@/models/evaluations';
import getRoute from '@/utils/getRoute/getRoute';

/* import KeywordsFilter from './KeywordsFilter'; */

const defaultParams = {
  sort: undefined,
  order: undefined,
  compliance: undefined,
  platformDisclosure: undefined,
  tags: undefined,
  hashtags: undefined,
  platform: undefined,
  createTime: undefined,
  keywords: undefined,
  keywordGroups: undefined,
  visualRecognition: undefined,
  adType: undefined,
  isRelevant: undefined,
  isRated: undefined
};

interface ToolbarProps {
  selectionModel: GridRowSelectionModel;
  setSelectionModel: (selectionModel: GridRowSelectionModel) => void;
  expiredStatus: 'none' | 'some' | 'all';
}

const Toolbar = ({
  selectionModel,
  setSelectionModel,
  expiredStatus
}: ToolbarProps) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const evaluationId = Number(useParams().evaluationId);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [reportData, setReportData] =
    useState<Evaluation.EvaluationPDFResponse | null>(null);

  const { params } = useUrlParams(
    getRoute.evaluations.DETAIL(evaluationId),
    defaultParams
  );

  const { data: evaluation } = useFetchEvaluation({
    id: evaluationId
  });

  const pdfPages = reportData ? [reportData] : [];
  const {
    url: pdfUrl,
    loading: isPdfLoading,
    error: pdfError
  } = useRenderPDF(pdfPages);

  const { mutate: exportEvaluationPosts, isPending: isCSVPending } =
    useExportEvaluationCSV({
      params: {
        evaluationId,
        ...params
      },
      onSuccess: (response) => {
        const blob = new Blob([response?.data], { type: 'text/csv' });

        const contentDisposition = response?.headers['content-disposition'];

        let filename = `evaluation_${evaluationId}_export.csv`; // Default filename
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=([^;]*)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, '').trim();
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        link.remove();
        alert.success(
          `The CSV file for "${evaluation?.title}" has been successfully downloaded.`
        );
        setSelectionModel([]);
      },
      onError: () => {
        alert.error('Something went wrong while exporting the CSV.');
      }
    });

  const { mutate: getReport, isPending: isExportingPDF } =
    useGetEvaluationReport({
      id: evaluationId,
      onSuccess: (response) => {
        setReportData(response.data);
      },
      onError: () => {
        alert.error('An error occurred while exporting the evaluation.');
      }
    });

  const { mutate: deletePosts, isPending: isDeleting } =
    useDeleteEvaluationPosts(
      (result) => {
        if (result?.status === 200) {
          const message =
            result?.data.deleted.length > 1
              ? 'Posts were successfully deleted!'
              : 'The post was successfully deleted!';
          alert.success(message);

          queryClient.invalidateQueries({ queryKey: ['evaluation'] });
          queryClient.invalidateQueries({ queryKey: ['posts'] });
          queryClient.invalidateQueries({
            queryKey: ['evaluations'],
            refetchType: 'inactive'
          });
          setIsDeleteDialogOpen(false);
          setSelectionModel([]);
        } else if (result?.response.data.message)
          alert.error(result?.response.data.message);
      },
      () => {
        alert.error('Something went wrong...');
      }
    );

  const handleConfirmDeletePost = () => {
    const payload = { evaluationId, postsIds: selectionModel };
    deletePosts(payload);
  };

  const handlePDFClickExport = () => {
    setIsExportDialogOpen(true);
  };

  const exportReport = () => {
    setReportData(null);
    getReport({ params, postsIds: selectionModel });
  };

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', `evaluation_${evaluationId}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert.success('The PDF file has been successfully downloaded.');
      setIsExportDialogOpen(false);
      setReportData(null);
      setSelectionModel([]);
    }
  };

  const isProcessing = isExportingPDF || isPdfLoading;
  const isSelectionInvalid =
    selectionModel.length === 0 || expiredStatus === 'all';
  const isCSVDisabled = expiredStatus === 'all' || isCSVPending;
  const isPDFDisabled =
    isSelectionInvalid || selectionModel.length > 100 || isProcessing;

  return (
    <>
      <GridToolbarContainer
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px 0 12px',
          height: '48px'
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexGrow={1}
          sx={{
            '& .MuiButton-root': {
              color: 'text.primary'
            },
            '& .MuiSvgIcon-root': {
              color: 'text.primary'
            }
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          {/*   <KeywordsFilter parameters={evaluation?.parameters || {}} /> */}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
        >
          <Button
            text="delete"
            variant={selectionModel.length ? 'contained' : 'text'}
            color="error"
            disabled={!selectionModel.length}
            onClick={() => setIsDeleteDialogOpen(true)}
            startIcon={<DeleteOutlined />}
            size="small"
          />
          <Stack
            sx={{
              '& .MuiButton-root': {
                color: 'text.primary'
              },
              '& .MuiSvgIcon-root': {
                color: 'text.primary'
              }
            }}
          >
            <GridToolbarExportContainer>
              {expiredStatus !== 'none' && (
                <MenuItem
                  disableRipple
                  sx={{
                    justifyContent: 'center',
                    cursor: 'default',
                    '&:hover': { backgroundColor: 'transparent' }
                  }}
                >
                  <Tooltip
                    title={
                      expiredStatus === 'all'
                        ? 'All selected posts have expired.'
                        : 'Some posts have expired. Only available ones will be exported.'
                    }
                    placement="left"
                    arrow
                  >
                    {expiredStatus === 'all' ? (
                      <Error fontSize="small" color="error" />
                    ) : (
                      <WarningAmber fontSize="small" color="warning" />
                    )}
                  </Tooltip>
                </MenuItem>
              )}
              <MenuItem
                disabled={isCSVDisabled}
                onClick={() =>
                  exportEvaluationPosts({
                    postIds: selectionModel
                  })
                }
              >
                {isCSVPending ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Exporting CSV</Typography>
                    <CircularProgress size={16} />
                  </Stack>
                ) : (
                  'Export CSV'
                )}
              </MenuItem>
              <MenuItem disabled={isPDFDisabled} onClick={handlePDFClickExport}>
                Export PDF
              </MenuItem>
            </GridToolbarExportContainer>
          </Stack>
        </Stack>
      </GridToolbarContainer>
      <Dialog
        title={selectionModel.length === 1 ? 'Delete Post' : 'Delete Posts'}
        onClose={isDeleting ? undefined : () => setIsDeleteDialogOpen(false)}
        dialogContent={
          selectionModel.length === 1 ? (
            <>
              <Typography>
                Are you sure you want to delete this post?
              </Typography>
              <Typography>You will not be able to recover it.</Typography>
            </>
          ) : (
            <>
              <Typography>
                Are you sure you want to delete {selectionModel.length} posts?
              </Typography>
              <Typography>You will not be able to recover these.</Typography>
            </>
          )
        }
        open={isDeleteDialogOpen}
        actions={
          <>
            <Button
              variant="text"
              text="cancel"
              disabled={isDeleting}
              onClick={() => setIsDeleteDialogOpen(false)}
            />
            <Button
              color="error"
              text="delete"
              onClick={handleConfirmDeletePost}
              loading={isDeleting}
            />
          </>
        }
      />
      <Dialog
        open={isExportDialogOpen}
        title="Export Posts"
        onClose={isProcessing ? undefined : () => setIsExportDialogOpen(false)}
        dialogContent={
          <>
            {(isExportingPDF || isPdfLoading) && (
              <Stack spacing={2}>
                <Typography>Generating PDF...</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography>
                    {isPdfLoading ? 'Rendering' : 'Processing'}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {pdfError && (
              <Typography color="error">
                An error occurred while generating the PDF: {String(pdfError)}
              </Typography>
            )}
            {!isProcessing && pdfUrl && (
              <Typography>Your PDF is ready! Click download button.</Typography>
            )}
            {!isProcessing && !pdfUrl && !pdfError && !reportData && (
              <Typography>
                Are you sure you want to export {selectionModel.length} post(s)?
              </Typography>
            )}
          </>
        }
        actions={
          <>
            <Button
              variant="text"
              text="cancel"
              disabled={isProcessing}
              onClick={() => {
                setIsExportDialogOpen(false);
                setReportData(null);
              }}
            />
            {!pdfUrl ? (
              <Button
                text="export"
                onClick={exportReport}
                loading={isProcessing}
              />
            ) : (
              <Button text="download" onClick={handleDownloadPDF} />
            )}
          </>
        }
      />
    </>
  );
};

export default Toolbar;
